import { useEffect } from 'react';
import type { InsightsResponse } from '../types/insights';

type InsightsTabContentProps = {
  data: InsightsResponse | null;
  isLoading: boolean;
  error: string | null;
  onRetry?: () => void;
};

export function InsightsTabContent({
  data,
  isLoading,
  error,
  onRetry,
}: InsightsTabContentProps) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  if (isLoading && !data && !error) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex flex-col items-center justify-center py-8 text-center text-gray-600">
          <div className="orders-stream-spinner mb-4" aria-hidden="true" />
          <p className="text-base">
            We&apos;re generating your personal insights. This can take a few
            minutes.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Please keep this tab open. Your insights will be ready when you
            return.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="text-center">
          <p className="text-red-600 mb-4">
            We couldn&apos;t load your insights right now.
          </p>
          {onRetry && (
            <button
              type="button"
              data-testid="insights-retry-button"
              onClick={onRetry}
              className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              Try again
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const confidenceColors = {
    high: 'bg-green-100 text-green-700',
    medium: 'bg-yellow-100 text-yellow-700',
    low: 'bg-gray-100 text-gray-600',
  };

  const loyaltyColors = {
    very_high: 'bg-purple-100 text-purple-700',
    high: 'bg-blue-100 text-blue-700',
    moderate: 'bg-gray-100 text-gray-600',
  };

  const trendIcons = {
    increasing: '↑',
    stable: '→',
    decreasing: '↓',
  };

  const trendColors = {
    increasing: 'text-green-600',
    stable: 'text-gray-500',
    decreasing: 'text-red-500',
  };

  return (
    <div className="space-y-6">
      {/* Summary Hero */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">{data.summary.headline}</h2>
        <p className="text-blue-100">{data.summary.description}</p>
      </div>

      {/* Key Insights */}
      {data.keyInsights && data.keyInsights.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="font-semibold text-amber-800 mb-2">
            Your Key Insights
          </h3>
          <ul className="space-y-1">
            {data.keyInsights.map((insight, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-sm text-amber-900"
              >
                <span className="text-amber-500">•</span>
                {insight}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* User Overview Stats */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold text-gray-900 mb-4">
          Your Shopping Overview
          {data.user.name && (
            <span className="text-gray-500 font-normal ml-2">
              ({data.user.name})
            </span>
          )}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-lg font-bold text-gray-900">
              ${data.user.totalSpend.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Total Spent</p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-lg font-bold text-gray-900">
              {data.user.totalOrders}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Orders</p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-lg font-bold text-gray-900">
              ${data.user.avgOrderValue.toFixed(0)}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Avg Order</p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-900 leading-snug">
              {data.user.purchaseFrequency}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">How Often</p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-900 leading-tight">
              {new Date(data.user.dataPeriod.start).toLocaleDateString()} –
              {new Date(data.user.dataPeriod.end).toLocaleDateString()}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Data Period</p>
          </div>
        </div>
      </div>

      {/* Life Stage */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900">Your Life Stage</h3>
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${confidenceColors[data.lifeStage.confidence]}`}
          >
            {data.lifeStage.confidence} confidence
          </span>
        </div>
        <p className="text-xl font-bold text-blue-600 mb-2">
          {data.lifeStage.stage}
        </p>
        <div className="flex flex-wrap gap-2">
          {data.lifeStage.signals.map((signal, i) => (
            <span
              key={i}
              className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded"
            >
              {signal}
            </span>
          ))}
        </div>
      </div>

      {/* Lifestyle Signals */}
      {data.lifestyleSignals && data.lifestyleSignals.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-4">Your Lifestyle</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.lifestyleSignals.map((signal, i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xl">{signal.icon}</span>
                  <span className="font-medium text-gray-900">
                    {signal.trait}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  {signal.description}
                </p>
                <div className="flex flex-wrap gap-1">
                  {signal.evidence.slice(0, 2).map((e, j) => (
                    <span
                      key={j}
                      className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded"
                    >
                      {e}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Categories */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold text-gray-900 mb-4">
          Your Spending by Category
        </h3>
        <div className="space-y-3">
          {data.categories.map((category, i) => (
            <div key={i} className="relative">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">
                    {category.name}
                  </span>
                  <span className={`text-sm ${trendColors[category.trend]}`}>
                    {trendIcons[category.trend]}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-gray-500">
                    {category.itemCount} items
                  </span>
                  <span
                    className="font-semibold"
                    style={{ color: category.color }}
                  >
                    ${category.spend.toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${category.percentOfTotal}%`,
                    backgroundColor: category.color,
                  }}
                />
              </div>
              <div className="flex items-center justify-between mt-1 text-xs text-gray-500">
                <span>{category.percentOfTotal.toFixed(1)}% of total</span>
                <span>{category.topBrands.slice(0, 3).join(', ')}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Archetypes */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold text-gray-900 mb-4">
          Your Shopper Personality
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.archetypes.map((archetype, i) => (
            <div
              key={i}
              className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200"
            >
              <h4 className="font-bold text-gray-900 text-lg mb-1">
                {archetype.name}
              </h4>
              <p className="text-sm text-gray-600 mb-3">
                {archetype.description}
              </p>
              <div className="flex flex-wrap gap-2 mb-3">
                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                  {archetype.category}
                </span>
                <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full capitalize">
                  {archetype.loyaltyPattern.replace(/_/g, ' ')}
                </span>
              </div>
              <ul className="text-xs text-gray-500 space-y-1">
                {archetype.evidence.slice(0, 3).map((e, j) => (
                  <li key={j} className="flex items-start gap-1">
                    <span className="text-gray-400">•</span>
                    {e}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Brand Loyalty */}
      {data.brandLoyalty && data.brandLoyalty.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-4">
            Your Favorite Brands
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.brandLoyalty.map((brand, i) => (
              <div
                key={i}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div>
                  <p className="font-medium text-gray-900">{brand.brand}</p>
                  <p className="text-xs text-gray-500">{brand.category}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">
                    {brand.purchaseCount}x
                  </p>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${loyaltyColors[brand.loyaltyScore]}`}
                  >
                    ${brand.totalSpend.toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Repeat Purchases */}
      {data.repeatPurchases && data.repeatPurchases.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-4">
            Things You Buy Regularly
          </h3>
          <div className="space-y-2">
            {data.repeatPurchases.slice(0, 5).map((item, i) => (
              <div
                key={i}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex-1">
                  <p className="font-medium text-gray-900 text-sm">
                    {item.productName}
                  </p>
                  <p className="text-xs text-gray-500">
                    Every ~{item.avgFrequencyDays} days • Last:{' '}
                    {item.lastPurchased}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-700">
                    {item.purchaseCount}x
                  </span>
                  {item.nextPurchaseEstimate && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                      Next: {item.nextPurchaseEstimate}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Spend Trends */}
      {data.spendTrends && data.spendTrends.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-4 text-base">
            Your Spending Over Time
          </h3>
          {(() => {
            const chartHeightPx = 140;
            const maxSpend = Math.max(
              ...data.spendTrends!.map((t) => t.spend),
              1
            );
            return (
              <div
                className="flex items-end gap-3"
                style={{ height: chartHeightPx + 56 }}
              >
                {data.spendTrends!.map((trend, i) => {
                  const barHeightPx = Math.max(
                    (trend.spend / maxSpend) * chartHeightPx,
                    4
                  );
                  return (
                    <div
                      key={i}
                      className="flex-1 flex flex-col items-center min-w-0 h-full"
                    >
                      <div
                        className="w-full flex-1 flex flex-col justify-end pb-1"
                        style={{ minHeight: chartHeightPx }}
                      >
                        <div
                          className="w-full max-w-[40px] mx-auto bg-blue-500 rounded-t transition-all"
                          style={{ height: barHeightPx }}
                          title={`$${trend.spend.toLocaleString()}`}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1 truncate w-full text-center shrink-0">
                        {trend.period}
                      </p>
                      <p className="text-xs font-semibold text-gray-700 shrink-0">
                        ${trend.spend.toLocaleString()}
                      </p>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* Shopping Habits */}
      {data.shoppingHabits && data.shoppingHabits.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-4">
            Your Shopping Habits
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.shoppingHabits.map((habit, i) => (
              <div
                key={i}
                className="bg-gray-50 rounded-lg p-4 border border-gray-200"
              >
                <h4 className="font-semibold text-gray-900 mb-1">
                  {habit.habit}
                </h4>
                <p className="text-sm text-gray-600 mb-3">
                  {habit.description}
                </p>
                <div className="flex items-start gap-2 bg-blue-50 rounded p-2">
                  <span className="text-blue-500 text-sm">💡</span>
                  <p className="text-xs text-blue-700">{habit.tip}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Saving Opportunities */}
      {data.savingOpportunities && data.savingOpportunities.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-4">
            Ways You Could Save
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.savingOpportunities.map((opportunity, i) => (
              <div
                key={i}
                className="bg-green-50 border border-green-200 rounded-lg p-4"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">
                    {opportunity.category}
                  </span>
                  <span className="text-xs text-gray-500">
                    ${opportunity.currentSpend.toLocaleString()} spent
                  </span>
                </div>
                <p className="text-sm text-gray-700 mb-2">
                  {opportunity.suggestion}
                </p>
                <p className="text-sm font-bold text-green-600">
                  Potential savings: {opportunity.potentialSavings}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {data.recommendations && data.recommendations.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-4">
            You Might Also Like
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.recommendations.map((rec, i) => (
              <div
                key={i}
                className="bg-purple-50 border border-purple-200 rounded-lg p-4"
              >
                <p className="text-xs text-purple-600 mb-1">
                  Based on: {rec.basedOn}
                </p>
                <p className="font-medium text-gray-900 mb-2">
                  {rec.recommendation}
                </p>
                <p className="text-sm text-gray-600">{rec.reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
